import dayjs, { type Dayjs } from 'dayjs';
import { StudentDataRequestRecipientFilter, StudentDataRequestStatus } from '@/types/composition-functions';
import type {
  CreateStudentDataRequestInput, StudentDataRequestAudienceInput, UpdateStudentDataRequestInput,
} from '@/types/composition-functions';
import type {
  MyStudentDataRequestsQuery, StudentDataRequestFieldsFragment, StudentDataRequestRecipientsQuery,
} from '@/types/operations';
import { csvCell } from '@/utils/aiActions';

/**
 * Data requests: the pure logic behind the admin pages (list, wizard, detail,
 * export) and the student's to-do and portal prompt, kept out of the
 * components so it can be unit-tested.
 */

export type DataRequest = StudentDataRequestFieldsFragment;
export type DataRequestRecipient = StudentDataRequestRecipientsQuery['studentDataRequestRecipients']['data'][number];
export type MyDataRequest = MyStudentDataRequestsQuery['myStudentDataRequests'][number];

/** Share of recipients who answered, 0–100, rounded down so a request is never shown done early. */
export function progressPercent(completed: number, total: number): number {
  if (!total) return 0;
  return Math.floor((completed / total) * 100);
}

/** "12 of 30 answered". */
export function progressText(request: Pick<DataRequest, 'completed_count' | 'recipients_count'>): string {
  return `${request.completed_count} of ${request.recipients_count} answered`;
}

/** When it is due, from the school's point of view: "Due today", "Due in 3 days", "2 days overdue". */
export function dueText(dueOn: string, today: Dayjs = dayjs()): string {
  const days = dayjs(dueOn).startOf('day').diff(today.startOf('day'), 'day');
  if (days === 0) return 'Due today';
  if (days === 1) return 'Due tomorrow';
  if (days > 1 && days <= 14) return `Due in ${days} days`;
  if (days > 14) return `Due ${dayjs(dueOn).format('D MMM YYYY')}`;
  return days === -1 ? '1 day overdue' : `${-days} days overdue`;
}

/** The request's state as a tag. */
export function statusTag(request: Pick<DataRequest, 'status' | 'is_overdue'>): {
  label: string; severity: 'info' | 'danger' | 'secondary';
} {
  if (request.status === StudentDataRequestStatus.Closed) return { label: 'Closed', severity: 'secondary' };
  if (request.is_overdue) return { label: 'Overdue', severity: 'danger' };
  return { label: 'Open', severity: 'info' };
}

export function formatDay(value: string | null | undefined): string {
  return value && dayjs(value).isValid() ? dayjs(value).format('D MMM YYYY') : '';
}

/** The calendar day the user picked, not its UTC equivalent (toISOString would send the day before in WAT). */
export function localDay(date: Date): string {
  return dayjs(date).format('YYYY-MM-DD');
}

// ── The "new request" wizard ────────────────────────────────────────────────

export interface DataRequestDraft {
  title: string;
  message: string;
  fields: Array<{ key: string; label: string; required: boolean }>;
  allStudents: boolean;
  classroomIds: string[];
  sectionIds: string[];
  programmeIds: string[];
  students: Array<{ id: string; label: string }>;
  dueOn: Date | null;
  blocksPortal: boolean;
  lockOnClose: boolean;
  includeLateJoiners: boolean;
}

export function emptyDataRequestDraft(): DataRequestDraft {
  return {
    title: '',
    message: '',
    fields: [],
    allStudents: true,
    classroomIds: [],
    sectionIds: [],
    programmeIds: [],
    students: [],
    dueOn: dayjs().add(7, 'day').startOf('day').toDate(),
    blocksPortal: false,
    lockOnClose: true,
    includeLateJoiners: false,
  };
}

export const WIZARD_STEPS = [
  { value: 'what', label: 'What to ask' },
  { value: 'who', label: 'Who to ask' },
  { value: 'when', label: 'Due date' },
  { value: 'preview', label: 'Check and send' },
] as const;

export type WizardStep = typeof WIZARD_STEPS[number]['value'];

/** The step each error key (the server's, or the wizard's own) belongs to. */
export function stepOfError(key: string): WizardStep {
  if (key === 'input.audience') return 'who';
  if (key === 'input.due_on' || key === 'input.blocks_portal') return 'when';
  return 'what';
}

export function hasAudience(draft: DataRequestDraft): boolean {
  return draft.allStudents || draft.classroomIds.length > 0 || draft.sectionIds.length > 0
    || draft.programmeIds.length > 0 || draft.students.length > 0;
}

/** The audience as the API takes it. */
export function audienceInput(draft: DataRequestDraft): StudentDataRequestAudienceInput {
  if (draft.allStudents) return { all_students: true };
  return {
    all_students: false,
    classroom_ids: draft.classroomIds,
    section_ids: draft.sectionIds,
    programme_ids: draft.programmeIds,
    student_ids: draft.students.map(s => s.id),
  };
}

export function createRequestInput(draft: DataRequestDraft): CreateStudentDataRequestInput {
  return {
    title: draft.title.trim(),
    message: draft.message.trim() || null,
    fields: draft.fields.map(f => ({ key: f.key, required: f.required })),
    audience: audienceInput(draft),
    due_on: draft.dueOn ? localDay(draft.dueOn) : null,
    blocks_portal: draft.blocksPortal,
    lock_on_close: draft.lockOnClose,
    include_late_joiners: draft.allStudents ? false : draft.includeLateJoiners,
  };
}

/**
 * The wizard's own checks for one step, keyed like the server's errors
 * ("input.title", …) so both show on the same field.
 */
export function draftErrors(draft: DataRequestDraft, step: WizardStep, today: Dayjs = dayjs()): Record<string, string> {
  const errors: Record<string, string> = {};
  if (step === 'what') {
    const title = draft.title.trim();
    if (!title) errors['input.title'] = 'Give the request a title.';
    else if (title.length > 150) errors['input.title'] = 'The title may not be longer than 150 characters.';
    if (draft.message.trim().length > 2000) errors['input.message'] = 'The message may not be longer than 2000 characters.';
    if (!draft.fields.length) errors['input.fields'] = 'Choose at least one field for students to fill in.';
    else if (!draft.fields.some(f => f.required)) {
      errors['input.fields'] = 'Mark at least one field as required, so you can tell who has answered.';
    }
  }
  if (step === 'who' && !hasAudience(draft)) {
    errors['input.audience'] = 'Choose who to ask: all students, classes, programmes or particular students.';
  }
  if (step === 'when') {
    if (!draft.dueOn) errors['input.due_on'] = 'Choose a due date.';
    else if (dayjs(draft.dueOn).startOf('day').isBefore(today.startOf('day'))) errors['input.due_on'] = 'Choose today or a later date.';
  }
  return errors;
}

/**
 * The chosen fields in the order offered, keeping each one's required flag;
 * a newly chosen field starts required.
 */
export function chooseFields(
  current: DataRequestDraft['fields'],
  choices: ReadonlyArray<{ key: string; label: string }>,
  keys: ReadonlyArray<string>,
): DataRequestDraft['fields'] {
  const kept = new Map(current.map(f => [f.key, f]));
  return choices.filter(c => keys.includes(c.key)).map(c => kept.get(c.key) ?? { key: c.key, label: c.label, required: true });
}

/** "All students (142 students)" for the wizard's last step. */
export function audienceSummaryText(allStudents: boolean, count: number | null | undefined): string {
  const who = allStudents ? 'All students' : 'The classes and students you chose';
  if (count === null || count === undefined) return who;
  return `${who} (${count} ${count === 1 ? 'student' : 'students'})`;
}

export interface DataRequestEditForm {
  title: string;
  message: string;
  dueOn: Date | null;
  lockOnClose: boolean;
  blocksPortal: boolean;
  includeLateJoiners: boolean;
}

/**
 * Only what changed, so editing the title of an overdue request does not
 * re-send a due date the server would refuse as past.
 */
export function editRequestInput(request: DataRequest, form: DataRequestEditForm): UpdateStudentDataRequestInput {
  const input: UpdateStudentDataRequestInput = {};
  if (form.title.trim() !== request.title) input.title = form.title.trim();
  if (form.message.trim() !== (request.message ?? '')) input.message = form.message.trim();
  const dueOn = form.dueOn ? localDay(form.dueOn) : null;
  if (dueOn && dueOn !== request.due_on) input.due_on = dueOn;
  if (form.lockOnClose !== request.lock_on_close) input.lock_on_close = form.lockOnClose;
  if (form.blocksPortal !== request.blocks_portal) input.blocks_portal = form.blocksPortal;
  if (form.includeLateJoiners !== request.include_late_joiners) input.include_late_joiners = form.includeLateJoiners;
  return input;
}

/** "142 students" / "No students match" for the audience preview. */
export function audienceCountText(count: number | null | undefined): string {
  if (count === null || count === undefined) return '';
  if (count === 0) return 'No students you can update match this audience.';
  return count === 1 ? 'This will reach 1 student.' : `This will reach ${count} students.`;
}

// ── Detail page ─────────────────────────────────────────────────────────────

/** A recipient's state, for the table. */
export function recipientStatus(row: Pick<DataRequestRecipient, 'completed_at'>): { label: string; severity: 'success' | 'warn' } {
  return row.completed_at ? { label: 'Answered', severity: 'success' } : { label: 'Waiting', severity: 'warn' };
}

/** "Reminded 2 times, last 21 Oct 2026", or empty. */
export function remindedText(row: Pick<DataRequestRecipient, 'reminder_count' | 'last_reminded_at'>): string {
  if (!row.reminder_count) return '';
  const times = row.reminder_count === 1 ? 'once' : `${row.reminder_count} times`;
  return row.last_reminded_at ? `Reminded ${times}, last ${formatDay(row.last_reminded_at)}` : `Reminded ${times}`;
}

/** What the recipients table says when it has no rows. */
export function recipientsEmptyText(filter: StudentDataRequestRecipientFilter, search: string): string {
  if (search.trim()) return 'No student matches that search.';
  if (filter === StudentDataRequestRecipientFilter.Pending) return 'Everyone has answered.';
  if (filter === StudentDataRequestRecipientFilter.Completed) return 'No one has answered yet.';
  return 'No students.';
}

/** What closing will do, for the confirmation. */
export function closeExplainerText(request: Pick<DataRequest, 'lock_on_close' | 'recipients_count' | 'completed_count'>): string {
  const waiting = request.recipients_count - request.completed_count;
  const lock = request.lock_on_close
    ? 'Students will no longer be able to change the answers it collected; they will need to ask the school.'
    : 'Students can still change what they entered on their profile.';
  const left = waiting > 0 ? ` ${waiting} ${waiting === 1 ? 'student has' : 'students have'} not answered yet.` : '';
  return `Reminders stop and the request leaves students' to-do lists. ${lock}${left} You can reopen it later.`;
}

export const RECIPIENTS_CSV_HEADERS = ['Name', 'Matric no', 'Class', 'Status', 'Answered on', 'Reminders', 'Still to fill in'];

/** The recipients as CSV; cells that a spreadsheet would read as a formula are made plain text. */
export function recipientsCsv(rows: ReadonlyArray<DataRequestRecipient>): string {
  const lines = rows.map(row => [
    csvCell(row.student_name),
    csvCell(row.matric_no),
    csvCell(row.class_name),
    csvCell(recipientStatus(row).label),
    csvCell(formatDay(row.completed_at)),
    String(row.reminder_count),
    csvCell(row.missing.join('; ')),
  ].join(','));
  // A byte-order mark, so Excel reads names with diacritics as UTF-8.
  return `\uFEFF${[RECIPIENTS_CSV_HEADERS.map(h => csvCell(h)).join(','), ...lines].join('\n')}`;
}

/** "update-your-records-answers.csv" */
export function recipientsCsvFilename(title: string): string {
  const slug = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60) || 'data-request';
  return `${slug}-answers.csv`;
}

// ── The student's side ──────────────────────────────────────────────────────

/** Where students answer: the profile form on their settings page. */
export const PROFILE_FORM_ROUTE = { name: 'student-settings', query: { tab: 'update' }, hash: '#student-profile-form' } as const;

/** Whether the student is on the page where they answer (the prompt steps aside there). */
export function isOnProfileForm(route: { name?: unknown; query?: Record<string, unknown> }): boolean {
  return route.name === 'student-settings' && route.query?.tab === 'update';
}

/**
 * Where the portal prompt never shows: the profile form (where they answer),
 * an exam (a timed exam is never held up by a profile prompt), and the whole
 * portal of a CBT-only school, whose students cannot reach the profile form.
 */
export function gateStepsAside(route: { name?: unknown; query?: Record<string, unknown> }, cbtOnlyPortal: boolean): boolean {
  return cbtOnlyPortal || isOnProfileForm(route) || route.name === 'student-cbt';
}

/** Requests the student can still act on: unanswered, with a required field they can fill in. */
export function actionableRequests(requests: ReadonlyArray<MyDataRequest>): MyDataRequest[] {
  return requests.filter(r => !r.completed && r.fields.some(f => f.required && !f.filled && f.fillable));
}

/**
 * Requests that keep the portal behind a prompt until answered. One whose
 * missing fields the student can no longer fill in (the school changed them)
 * does not hold them up.
 */
export function blockingRequests(requests: ReadonlyArray<MyDataRequest>): MyDataRequest[] {
  return actionableRequests(requests).filter(r => r.blocks_portal);
}

/** Labels of the required fields still empty. */
export function pendingFieldLabels(request: Pick<MyDataRequest, 'fields'>): string[] {
  return request.fields.filter(f => f.required && !f.filled).map(f => f.label);
}

/** "2 of 3 filled in". */
export function myRequestProgress(request: Pick<MyDataRequest, 'fields'>): string {
  const required = request.fields.filter(f => f.required);
  return `${required.filter(f => f.filled).length} of ${required.length} filled in`;
}
